import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiFileText, FiPlus, FiTrash2, FiSearch, FiRefreshCw, 
  FiDownload 
} from 'react-icons/fi';
import { inventoryAPI } from '../../services/api';
import { toast } from 'react-toastify';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: '#64748b', bg: '#f1f5f9' },
  signed: { label: 'Signé', color: '#2563eb', bg: '#dbeafe' },
  finalized: { label: 'Finalisé', color: '#16a34a', bg: '#dcfce7' },
};

const InventoriesList = () => {
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloadingPV, setDownloadingPV] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const navigate = useNavigate();

  const fetchInventories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.list({
        type: typeFilter !== 'all' ? typeFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined,
      });
      setInventories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Erreur lors du chargement des états des lieux');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInventories();
    }, 200);
    return () => clearTimeout(timer);
  }, [fetchInventories]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet état des lieux ?')) return;
    try {
      await inventoryAPI.delete(id);
      toast.success('État des lieux supprimé avec succès');
      fetchInventories();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadPDF = async (id, type, e) => {
    e.stopPropagation();
    setDownloadingPDF(id);
    try {
      const res = await inventoryAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `etat-des-lieux-${type}-${id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setDownloadingPDF(null);
    }
  };

  const handleDownloadComparisonPV = async (leaseId, e) => {
    e.stopPropagation();
    if (!leaseId) return toast.warn("Cet état des lieux n'est pas lié à un contrat.");
    setDownloadingPV(leaseId);
    try {
      const res = await inventoryAPI.downloadComparisonPDF(leaseId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PV-Comparatif-${leaseId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PV comparatif téléchargé avec succès');
    } catch {
      toast.error("PV indisponible : vérifiez qu'un état d'entrée ET de sortie existent pour ce contrat.");
    } finally {
      setDownloadingPV(null);
    }
  };

  // Group check-ins and check-outs by lease to identify eligible PVs
  const leaseMap = inventories.reduce((acc, inv) => {
    if (inv.lease_contract_id) {
      acc[inv.lease_contract_id] = acc[inv.lease_contract_id] || { hasIn: false, hasOut: false };
      if (inv.type === 'check-in') acc[inv.lease_contract_id].hasIn = true;
      if (inv.type === 'check-out') acc[inv.lease_contract_id].hasOut = true;
    }
    return acc;
  }, {});

  const canGeneratePV = (inv) => {
    if (!inv.lease_contract_id) return false;
    const lease = leaseMap[inv.lease_contract_id];
    return lease?.hasIn && lease?.hasOut;
  };

  // Summary counts
  const totalCount = inventories.length;
  const inCount = inventories.filter(i => i.type === 'check-in').length;
  const outCount = inventories.filter(i => i.type === 'check-out').length;
  const signedCount = inventories.filter(i => i.status === 'signed' || i.status === 'finalized').length;

  return (
    <div style={{ padding: '24px', maxWidth: 1300, margin: '0 auto' }}>
      {/* ── Page Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiFileText color="var(--primary, #2563eb)" /> États des lieux
          </h1>
          <p style={{ color: 'var(--text-secondary, #64748b)', margin: '6px 0 0', fontSize: 14 }}>
            Gestion des constats d'entrée, de sortie et PV comparatifs de votre agence.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button 
            className="btn btn-outline" 
            onClick={fetchInventories} 
            disabled={loading}
            title="Actualiser la liste"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiRefreshCw className={loading ? 'spin' : ''} /> Actualiser
          </button>
          <Link to="/inventories/new" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiPlus /> Nouvel état des lieux
          </Link>
        </div>
      </div>

      {/* ── Metric Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ padding: '16px 20px', borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Total</div>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{totalCount}</div>
        </div>
        <div className="card" style={{ padding: '16px 20px', borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, textTransform: 'uppercase' }}>Entrées</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#16a34a', marginTop: 4 }}>{inCount}</div>
        </div>
        <div className="card" style={{ padding: '16px 20px', borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600, textTransform: 'uppercase' }}>Sorties</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#d97706', marginTop: 4 }}>{outCount}</div>
        </div>
        <div className="card" style={{ padding: '16px 20px', borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 600, textTransform: 'uppercase' }}>Signés</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#2563eb', marginTop: 4 }}>{signedCount}</div>
        </div>
      </div>

      {/* ── Filters & Search ── */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20, borderRadius: 10 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Search box */}
          <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
            <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              className="form-control"
              style={{ paddingLeft: 36 }}
              placeholder="Rechercher par bien, adresse, n° contrat ou agent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Type Filter */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>Type :</span>
            {[
              ['all', 'Tous'],
              ['check-in', 'Entrées'],
              ['check-out', 'Sorties']
            ].map(([val, label]) => (
              <button
                key={val}
                type="button"
                className={`btn btn-sm ${typeFilter === val ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setTypeFilter(val)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>Statut :</span>
            <select
              className="form-control"
              style={{ width: 'auto', padding: '6px 12px', fontSize: 13 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="draft">Brouillons</option>
              <option value="signed">Signés</option>
              <option value="finalized">Finalisés</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Table / List ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-secondary)' }}>Chargement des états des lieux...</p>
        </div>
      ) : inventories.length === 0 ? (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center', borderRadius: 10 }}>
          <FiFileText size={48} style={{ color: '#cbd5e1', marginBottom: 16 }} />
          <h3 style={{ margin: '0 0 8px', color: '#1e293b' }}>Aucun état des lieux trouvé</h3>
          <p style={{ color: 'var(--text-secondary)', maxWidth: 450, margin: '0 auto 20px', fontSize: 14 }}>
            {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
              ? 'Aucun résultat ne correspond à vos critères de recherche.'
              : 'Votre agence n\'a pas encore enregistré d\'état des lieux.'}
          </p>
          <Link to="/inventories/new" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FiPlus /> Créer le premier état des lieux
          </Link>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg, #f8fafc)', borderBottom: '2px solid var(--border, #e2e8f0)' }}>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Bien / Unité</th>
                <th style={thStyle}>Contrat</th>
                <th style={thStyle}>Date de visite</th>
                <th style={thStyle}>Agent</th>
                <th style={thStyle}>Statut</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {inventories.map((inv) => {
                const statusStyle = STATUS_CONFIG[inv.status] || { label: inv.status, color: '#64748b', bg: '#f1f5f9' };
                return (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/inventories/${inv.id}`)}
                    style={{ borderBottom: '1px solid var(--border, #e2e8f0)', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg, #f8fafc)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          background: inv.type === 'check-in' ? '#dcfce7' : '#fef3c7',
                          color: inv.type === 'check-in' ? '#166534' : '#92400e',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        {inv.type === 'check-in' ? '↘ Entrée' : '↗ Sortie'}
                      </span>
                    </td>

                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
                        {inv.property_title || 'Bien non renseigné'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)', marginTop: 2 }}>
                        {inv.unit_number ? (
                          <span>
                            <span style={{ background: '#ede9fe', color: '#7c3aed', padding: '1px 6px', borderRadius: 4, fontWeight: 600, fontSize: 10, marginRight: 6 }}>
                              Unité {inv.unit_number} {inv.unit_type ? `(${inv.unit_type})` : ''}
                            </span>
                            {inv.property_address || ''}
                          </span>
                        ) : (
                          inv.property_address || ''
                        )}
                      </div>
                    </td>

                    <td style={tdStyle}>
                      {inv.contract_number ? (
                        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'var(--primary, #2563eb)' }}>
                          {inv.contract_number}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: 12 }}>—</span>
                      )}
                    </td>

                    <td style={tdStyle}>
                      {inv.conducted_date ? new Date(inv.conducted_date).toLocaleDateString('fr-FR') : '—'}
                    </td>

                    <td style={tdStyle}>
                      <span style={{ fontSize: 12, color: '#334155' }}>
                        {inv.conducted_by_name || '—'}
                      </span>
                    </td>

                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          background: statusStyle.bg,
                          color: statusStyle.color,
                        }}
                      >
                        {statusStyle.label}
                      </span>
                    </td>

                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                        {/* Télécharger PDF */}
                        <button
                          type="button"
                          title="Télécharger l'état des lieux en PDF"
                          onClick={(e) => handleDownloadPDF(inv.id, inv.type, e)}
                          disabled={downloadingPDF === inv.id}
                          style={actionBtn('#2563eb')}
                        >
                          {downloadingPDF === inv.id ? '…' : <FiDownload size={14} />}
                        </button>

                        {/* PV Comparatif (si check-out et check-in existent pour ce contrat) */}
                        {canGeneratePV(inv) && inv.type === 'check-out' && (
                          <button
                            type="button"
                            title="Télécharger le Procès-Verbal Comparatif"
                            onClick={(e) => handleDownloadComparisonPV(inv.lease_contract_id, e)}
                            disabled={downloadingPV === inv.lease_contract_id}
                            style={actionBtn('#7c3aed')}
                          >
                            {downloadingPV === inv.lease_contract_id ? '…' : '⚖️'}
                          </button>
                        )}

                        {/* Supprimer */}
                        <button
                          type="button"
                          title="Supprimer cet état des lieux"
                          onClick={(e) => handleDelete(inv.id, e)}
                          style={actionBtn('#dc2626')}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-secondary, #64748b)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle = {
  padding: '14px 16px',
  fontSize: 13,
  verticalAlign: 'middle',
};

const actionBtn = (color) => ({
  width: 32,
  height: 32,
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  background: color + '15',
  color: color,
  fontSize: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.2s',
});

export default InventoriesList;
